import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Facebook, Instagram, Users, Eye, Heart, MessageCircle, TrendingUp, Share2, MousePointer, Loader2, X, Trophy, Film, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (!n && n !== 0) return '0';
  if (n >= 1000000) return `${(n/1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n/1000).toFixed(1)}K`;
  return String(n);
};

export default function OrganicDashboard() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('facebook');
  const [pages, setPages] = useState([]);
  const [igAccounts, setIgAccounts] = useState([]);
  const [fbData, setFbData] = useState(null);
  const [igData, setIgData] = useState(null);
  const [loadingFb, setLoadingFb] = useState(false);
  const [loadingIg, setLoadingIg] = useState(false);
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [savedPageId, setSavedPageId] = useState(null);
  const [savedIgId, setSavedIgId] = useState(null);
  const [fbReels, setFbReels] = useState([]);
  const [igReels, setIgReels] = useState([]);
  const [loadingReels, setLoadingReels] = useState(false);
  const [reelsSubTab, setReelsSubTab] = useState('facebook');
  const [postsSubTab, setPostsSubTab] = useState('facebook');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { if (clientId) loadData(); }, [clientId]);

  const loadData = async () => {
    setLoading(true);
    // Reset de estado al cambiar de marca (evita mostrar datos viejos durante carga)
    setFbData(null);
    setIgData(null);
    setPages([]);
    setIgAccounts([]);
    setSavedPageId(null);
    setSavedIgId(null);
    setFbReels([]);
    setIgReels([]);
    setShowPageSelector(false);
    try {
      // /organic/pages devuelve is_linked: true si la marca ya tiene page asignada
      // (post-refactor Fase 8: la verdad vive en social_connections, no en /meta/status)
      const pagesRes = await axios.get(`${API}/organic/pages?client_id=${clientId}`, { headers });
      const p = pagesRes.data.pages || [];
      const ig = pagesRes.data.instagram || [];
      const isLinked = pagesRes.data.is_linked === true;
      setPages(p);
      setIgAccounts(ig);

      if (isLinked && p.length > 0) {
        // Marca con page asignada: cargar directo sus métricas
        const fpId = p[0].id;
        const igId = ig.length > 0 ? ig[0].id : null;
        setSavedPageId(fpId);
        setSavedIgId(igId);
        fetchFb(fpId);
        if (igId) fetchIg(igId);
      } else if (!isLinked && p.length > 0) {
        // Marca virgen con pages de admin directo disponibles: mostrar selector
        setShowPageSelector(true);
      }
      // Si no hay pages, el render muestra "Sin páginas conectadas"
    } catch(e) { console.error('loadData error:', e); }
    setLoading(false);
  };

  const fetchFb = async (pageId) => {
    setLoadingFb(true);
    try {
      const r = await axios.get(`${API}/organic/facebook?client_id=${clientId}&page_id=${pageId}`, { headers });
      console.log('FB data:', r.data);
      setFbData(r.data);
    } catch(e) { console.error('FB error:', e); }
    setLoadingFb(false);
  };

  const fetchIg = async (igId) => {
    setLoadingIg(true);
    try {
      const r = await axios.get(`${API}/organic/instagram?client_id=${clientId}&ig_id=${igId}`, { headers });
      console.log('IG data:', r.data);
      setIgData(r.data);
    } catch(e) { console.error('IG error:', e); }
    setLoadingIg(false);
  };

  const fetchReels = async (fpOverride = null, igOverride = null) => {
    const fpId = fpOverride || savedPageId;
    const igId = igOverride || savedIgId;
    setLoadingReels(true);
    try {
      const [fbR, igR] = await Promise.allSettled([
        fpId ? axios.get(`${API}/organic/reels/facebook?client_id=${clientId}&page_id=${fpId}`, { headers }) : Promise.resolve({data:{reels:[]}}),
        igId ? axios.get(`${API}/organic/reels/instagram?client_id=${clientId}&ig_id=${igId}`, { headers }) : Promise.resolve({data:{reels:[]}})
      ]);
      if (fbR.status === 'fulfilled') { setFbReels(fbR.value.data.reels || []); }
      if (igR.status === 'fulfilled') { setIgReels(igR.value.data.reels || []); }
    } catch(e) { console.error('Reels error:', e); }
    setLoadingReels(false);
  };

  const selectPage = async (page) => {
    const ig = igAccounts.find(a => a.page_id === page.id);
    try {
      await axios.post(`${API}/organic/save-page?client_id=${clientId}`, {
        page_id: page.id, page_name: page.name,
        ig_id: ig ? ig.id : '', ig_username: ig ? ig.username : ''
      }, { headers });
      setSavedPageId(page.id);
      setSavedIgId(ig ? ig.id : null);
      setShowPageSelector(false);
      fetchFb(page.id);
      if (ig) fetchIg(ig.id);
    } catch(e) { console.error('Save error:', e); }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 text-sm">Cargando datos orgánicos...</p>
      </div>
    </DashboardLayout>
  );

  if (!clientId || (pages.length === 0 && !savedPageId)) return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className={`text-2xl font-bold mb-2 ${theme.text}`}>Dashboard Orgánico</h1>
        <p className={`${theme.textSecondary} mb-6`}>Facebook e Instagram — contenido orgánico</p>
        <div className={`${theme.card} rounded-2xl p-12 text-center`}>
          <p className={`text-lg font-semibold ${theme.text} mb-2`}>Sin páginas conectadas</p>
          <p className={theme.textSecondary}>Reconecta Meta Ads con permisos de páginas para ver métricas orgánicas.</p>
        </div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Dashboard Orgánico</h1>
            <p className={theme.textSecondary}>Facebook e Instagram — contenido orgánico</p>
          </div>
          {savedPageId && (
            <button onClick={() => setShowPageSelector(true)}
              className="text-sm text-indigo-500 hover:text-indigo-700 font-medium">
              Cambiar página
            </button>
          )}
        </div>

        {/* Page Selector Modal */}
        {showPageSelector && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{background:'white',borderRadius:16,padding:32,width:520,maxWidth:'90vw',position:'relative'}}>
              <button onClick={() => setShowPageSelector(false)} style={{position:'absolute',top:12,right:12,border:'none',background:'none',cursor:'pointer'}}>
                <X size={20} color="#94a3b8" />
              </button>
              <h3 style={{fontSize:18,fontWeight:700,color:'#1e293b',margin:'0 0 8px'}}>Selecciona la página para este cliente</h3>
              <p style={{color:'#64748b',fontSize:14,marginBottom:20}}>Elige la página de Facebook e Instagram.</p>
              <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:400,overflowY:'auto'}}>
                {pages.map(page => {
                  const ig = igAccounts.find(a => a.page_id === page.id);
                  return (
                    <div key={page.id} onClick={() => selectPage(page)}
                      style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderRadius:12,
                        border:`2px solid ${savedPageId===page.id?'#6366F1':'#e2e8f0'}`,cursor:'pointer',
                        background:savedPageId===page.id?'#F0F0FF':'white',transition:'all 0.2s'}}
                      onMouseEnter={e => {if(savedPageId!==page.id) e.currentTarget.style.borderColor='#6366F1'}}
                      onMouseLeave={e => {if(savedPageId!==page.id) e.currentTarget.style.borderColor='#e2e8f0'}}>
                      {page.picture && <img src={page.picture} alt="" style={{width:40,height:40,borderRadius:'50%'}} />}
                      <div style={{flex:1}}>
                        <p style={{margin:0,fontWeight:600,color:'#1e293b',fontSize:14}}>{page.name}</p>
                        <p style={{margin:0,color:'#94a3b8',fontSize:12}}>{fmt(page.fan_count)} fans{ig ? ` · @${ig.username}` : ''}</p>
                      </div>
                      <div style={{display:'flex',gap:6}}>
                        <span style={{background:'#1877F2',color:'white',fontSize:10,padding:'2px 8px',borderRadius:12,fontWeight:600}}>FB</span>
                        {ig && <span style={{background:'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)',color:'white',fontSize:10,padding:'2px 8px',borderRadius:12,fontWeight:600}}>IG</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab('facebook')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${tab === 'facebook' ? 'bg-blue-600 text-white shadow-lg' : `${theme.card} ${theme.text} border border-gray-200`}`}>
            <Facebook className="w-4 h-4" /> Facebook
          </button>
          <button onClick={() => setTab('instagram')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${tab === 'instagram' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' : `${theme.card} ${theme.text} border border-gray-200`}`}>
            <Instagram className="w-4 h-4" /> Instagram
          </button>
          <button onClick={() => setTab('posts')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${tab === 'posts' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg' : `${theme.card} ${theme.text} border border-gray-200`}`}>
            <Trophy className="w-4 h-4" /> Publicaciones
          </button>
          <button onClick={() => { setTab('reels'); if (!igData && savedIgId) fetchIg(savedIgId); fetchReels(); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${tab === 'reels' ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg' : `${theme.card} ${theme.text} border border-gray-200`}`}>
            <Film className="w-4 h-4" /> Reels
          </button>
        </div>

        {/* FACEBOOK TAB */}
        {tab === 'facebook' && (
          <div>
            {loadingFb ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div className={`${theme.card} rounded-xl p-4 border border-gray-100`}>
                    <div className="flex items-center gap-2 mb-1 text-red-500"><Heart className="w-5 h-5" /><span className="text-xs font-medium uppercase tracking-wide">Engagement</span></div>
                    <p className={`text-2xl font-bold ${theme.text}`}>{fmt(fbData?.insights?.page_post_engagements?.total || 0)}</p>
                  </div>
                  <div className={`${theme.card} rounded-xl p-4 border border-gray-100`}>
                    <div className="flex items-center gap-2 mb-1 text-blue-500"><Eye className="w-5 h-5" /><span className="text-xs font-medium uppercase tracking-wide">Vistas de Página</span></div>
                    <p className={`text-2xl font-bold ${theme.text}`}>{fmt(fbData?.insights?.page_views_total?.total || 0)}</p>
                  </div>
                  <div className={`${theme.card} rounded-xl p-4 border border-gray-100`}>
                    <div className="flex items-center gap-2 mb-1 text-purple-500"><TrendingUp className="w-5 h-5" /><span className="text-xs font-medium uppercase tracking-wide">Video Views</span></div>
                    <p className={`text-2xl font-bold ${theme.text}`}>{fmt(fbData?.insights?.page_video_views?.total || 0)}</p>
                  </div>
                </div>

              </>
            )}
          </div>
        )}

        {/* INSTAGRAM TAB */}
        {tab === 'instagram' && (
          <div>
            {loadingIg ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-pink-500" /></div>
            ) : !savedIgId ? (
              <div className={`${theme.card} rounded-2xl p-12 text-center`}>
                <Instagram className="w-12 h-12 mx-auto text-pink-400 mb-3" />
                <p className={`text-lg font-semibold ${theme.text} mb-2`}>Sin cuenta de Instagram</p>
                <p className={theme.textSecondary}>No se encontró una cuenta de Instagram Business vinculada.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className={`${theme.card} rounded-xl p-4 border border-gray-100`}>
                    <div className="flex items-center gap-2 mb-1 text-purple-500"><Eye className="w-5 h-5" /><span className="text-xs font-medium uppercase tracking-wide">Alcance</span></div>
                    <p className={`text-2xl font-bold ${theme.text}`}>{fmt(igData?.insights?.reach?.total || 0)}</p>
                  </div>
                  <div className={`${theme.card} rounded-xl p-4 border border-gray-100`}>
                    <div className="flex items-center gap-2 mb-1 text-pink-500"><Users className="w-5 h-5" /><span className="text-xs font-medium uppercase tracking-wide">Seguidores</span></div>
                    <p className={`text-2xl font-bold ${theme.text}`}>{fmt(igData?.insights?.follower_count?.total || 0)}</p>
                  </div>
                </div>

              </>
            )}
          </div>
        )}


        {/* PUBLICACIONES TAB */}
        {tab === 'posts' && (
          <div>
            {/* Sub-tabs Facebook / Instagram */}
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={() => setPostsSubTab('facebook')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  postsSubTab === 'facebook'
                    ? 'bg-blue-500 text-white'
                    : `${theme.textSecondary} ${theme.cardHover} border ${theme.border}`
                }`}
              >
                <Facebook className="w-4 h-4" />
                Facebook <span className="opacity-70">({(fbData?.posts || []).length})</span>
              </button>
              <button
                onClick={() => setPostsSubTab('instagram')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  postsSubTab === 'instagram'
                    ? 'bg-pink-500 text-white'
                    : `${theme.textSecondary} ${theme.cardHover} border ${theme.border}`
                }`}
              >
                <Instagram className="w-4 h-4" />
                Instagram <span className="opacity-70">({(igData?.media || []).length})</span>
              </button>
            </div>

            {postsSubTab === 'facebook' ? (
              <>
                <h3 className={`text-lg font-bold mb-4 ${theme.text} flex items-center gap-2`}><Trophy className="w-5 h-5 text-yellow-500" />Top 4 Publicaciones de Facebook</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  {[...(fbData?.posts || [])].sort((a, b) => ((b.likes || 0) + (b.comments || 0)) - ((a.likes || 0) + (a.comments || 0))).slice(0, 4).map((post, i) => (
                    <a key={i} href={post.permalink} target="_blank" rel="noreferrer" className={`${theme.card} rounded-xl overflow-hidden border-2 ${i === 0 ? 'border-yellow-400 shadow-lg' : i === 1 ? 'border-gray-300' : i === 2 ? 'border-amber-600' : 'border-gray-100'} hover:shadow-lg transition-shadow relative`}>
                      <div className={`absolute top-2 left-2 z-10 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-400 text-yellow-900' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'}`}>#{i + 1}</div>
                      {post.image ? <img src={post.image} alt="" className="w-full h-32 object-cover" /> : <div className="w-full h-32 bg-blue-50 flex items-center justify-center"><Facebook className="w-6 h-6 text-blue-300" /></div>}
                      <div className="p-2">
                        <p className={`text-xs ${theme.textSecondary} mb-1 line-clamp-1`}>{post.message || '(sin texto)'}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1 font-semibold"><Heart className="w-3 h-3 text-red-400" />{post.likes || 0}</span>
                          <span className="flex items-center gap-1 font-semibold"><MessageCircle className="w-3 h-3 text-blue-400" />{post.comments || 0}</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>

                <h3 className={`text-lg font-bold mb-3 ${theme.text}`}>Publicaciones Recientes</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {(fbData?.posts || []).length > 0 ? fbData.posts.map((post, i) => (
                    <a key={i} href={post.permalink} target="_blank" rel="noreferrer" className={`${theme.card} rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow`}>
                      {post.image ? <img src={post.image} alt="" className="w-full h-40 object-cover" /> : <div className="w-full h-40 bg-blue-50 flex items-center justify-center"><Facebook className="w-8 h-8 text-blue-300" /></div>}
                      <div className="p-3">
                        <p className={`text-xs ${theme.textSecondary} mb-2 line-clamp-2`}>{post.message || '(sin texto)'}</p>
                        <p className="text-xs text-gray-400 mb-2">{post.created_time ? new Date(post.created_time).toLocaleDateString('es-CL') : ''}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-400" />{post.likes || 0}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-blue-400" />{post.comments || 0}</span>
                        </div>
                      </div>
                    </a>
                  )) : <p className={theme.textSecondary}>No hay publicaciones recientes.</p>}
                </div>
              </>
            ) : (
              <>
                <h3 className={`text-lg font-bold mb-4 ${theme.text} flex items-center gap-2`}><Trophy className="w-5 h-5 text-yellow-500" />Top 4 Publicaciones de Instagram</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  {[...(igData?.media || [])].sort((a, b) => ((b.likes || 0) + (b.comments || 0)) - ((a.likes || 0) + (a.comments || 0))).slice(0, 4).map((m, i) => (
                    <a key={i} href={m.permalink} target="_blank" rel="noreferrer" className={`${theme.card} rounded-xl overflow-hidden border-2 ${i === 0 ? 'border-yellow-400 shadow-lg' : i === 1 ? 'border-gray-300' : i === 2 ? 'border-amber-600' : 'border-gray-100'} hover:shadow-lg transition-shadow relative`}>
                      <div className={`absolute top-2 left-2 z-10 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-400 text-yellow-900' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'}`}>#{i + 1}</div>
                      {m.media_url ? <img src={m.media_url} alt="" className="w-full h-32 object-cover" onError={(e) => { e.target.style.display="none"; e.target.nextSibling && (e.target.nextSibling.style.display="flex"); }} /> : null}<div className="w-full h-32 bg-pink-50 items-center justify-center" style={{display: m.media_url ? "none" : "flex"}}><Instagram className="w-6 h-6 text-pink-300" /></div>
                      <div className="p-2">
                        <p className={`text-xs ${theme.textSecondary} mb-1 line-clamp-1`}>{m.caption || ''}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1 font-semibold"><Heart className="w-3 h-3 text-red-400" />{m.likes || 0}</span>
                          <span className="flex items-center gap-1 font-semibold"><MessageCircle className="w-3 h-3 text-blue-400" />{m.comments || 0}</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>

                <h3 className={`text-lg font-bold mb-3 ${theme.text}`}>Publicaciones Recientes</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {(igData?.media || []).length > 0 ? igData.media.map((m, i) => (
                    <a key={i} href={m.permalink} target="_blank" rel="noreferrer" className={`${theme.card} rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow`}>
                      {m.media_url ? <img src={m.media_url} alt="" className="w-full h-40 object-cover" onError={(e) => { e.target.style.display="none"; e.target.nextSibling && (e.target.nextSibling.style.display="flex"); }} /> : null}<div className="w-full h-40 bg-pink-50 items-center justify-center" style={{display: m.media_url ? "none" : "flex"}}><Instagram className="w-8 h-8 text-pink-300" /></div>
                      <div className="p-3">
                        <p className={`text-xs ${theme.textSecondary} mb-2 line-clamp-2`}>{m.caption || ''}</p>
                        <div className="flex gap-3 text-xs">
                          <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-400" />{fmt(m.likes)}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-blue-400" />{fmt(m.comments)}</span>
                        </div>
                      </div>
                    </a>
                  )) : <p className={theme.textSecondary}>No hay publicaciones recientes.</p>}
                </div>
              </>
            )}
          </div>
        )}
        {/* REELS TAB */}
        {tab === 'reels' && (
          <div>
            {loadingReels ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
            ) : (
              <>
                {/* FB Reels */}
                {/* Sub-tabs Facebook / Instagram */}
                <div className="flex items-center gap-2 mb-6">
                  <button
                    onClick={() => setReelsSubTab('facebook')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      reelsSubTab === 'facebook'
                        ? 'bg-blue-500 text-white'
                        : `${theme.textSecondary} ${theme.cardHover} border ${theme.border}`
                    }`}
                  >
                    <Facebook className="w-4 h-4" />
                    Facebook <span className="opacity-70">({fbReels.length})</span>
                  </button>
                  <button
                    onClick={() => setReelsSubTab('instagram')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      reelsSubTab === 'instagram'
                        ? 'bg-pink-500 text-white'
                        : `${theme.textSecondary} ${theme.cardHover} border ${theme.border}`
                    }`}
                  >
                    <Instagram className="w-4 h-4" />
                    Instagram <span className="opacity-70">({igReels.length})</span>
                  </button>
                </div>

                {/* Contenido según sub-tab */}
                {reelsSubTab === 'facebook' ? (
                  fbReels.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {fbReels.map((reel, i) => (
                        <a key={i} href={reel.permalink} target="_blank" rel="noreferrer" className={`${theme.card} rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow relative`}>
                          <div className="absolute top-2 right-2 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"><Play className="w-3 h-3" />Reel</div>
                          {reel.thumbnail ? <img src={reel.thumbnail} alt="" className="w-full" style={{aspectRatio: "9/16", objectFit: "cover"}} /> : <div className="w-full bg-blue-50 flex items-center justify-center" style={{aspectRatio: "9/16"}}><Film className="w-8 h-8 text-blue-300" /></div>}
                          <div className="p-3">
                            <p className={`text-xs ${theme.textSecondary} mb-2 line-clamp-2`}>{reel.description || '(sin descripción)'}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-blue-400" />{fmt(reel.views)}</span>
                              <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-400" />{fmt(reel.likes)}</span>
                              <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-blue-400" />{fmt(reel.comments)}</span>
                              <span className="flex items-center gap-1"><Share2 className="w-3 h-3 text-purple-400" />{fmt(reel.shares)}</span>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : <p className={theme.textSecondary}>No hay Reels de Facebook disponibles.</p>
                ) : (
                  igReels.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {igReels.map((reel, i) => (
                        <a key={i} href={reel.permalink} target="_blank" rel="noreferrer" className={`${theme.card} rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow relative`}>
                          <div className="absolute top-2 right-2 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"><Play className="w-3 h-3" />Reel</div>
                          {(reel.thumbnail || reel.media_url) ? <img src={reel.thumbnail || reel.media_url} alt="" className="w-full" style={{aspectRatio: "9/16", objectFit: "cover"}} onError={(e) => { e.target.style.display="none"; e.target.nextSibling && (e.target.nextSibling.style.display="flex"); }} /> : null}
                          <div className="w-full bg-pink-50 items-center justify-center" style={{aspectRatio: "9/16", display: (reel.thumbnail || reel.media_url) ? "none" : "flex"}}><Film className="w-8 h-8 text-pink-300" /></div>
                          <div className="p-3">
                            <p className={`text-xs ${theme.textSecondary} mb-2 line-clamp-2`}>{reel.caption || '(sin descripción)'}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-400" />{fmt(reel.likes)}</span>
                              <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-blue-400" />{fmt(reel.comments)}</span>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : <p className={theme.textSecondary}>No hay Reels de Instagram disponibles.</p>
                )}
              </>
            )}
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
